import { ExpandableItem } from "PFApp/booking/parts/reports/top_actions_panel/export_wizard/components/expandable_item";
import { ExportType } from "PFApp/booking/types/export_booking";
import { useFilters } from "PFApp/use_filtered_collection/use_filters";
import { Filters, Value } from "PFTypes";
import React, { useCallback, useMemo, useState } from "react";

import { useExportWizardModalContext } from "../../../../export_wizard_modal_provider";
import { decamelizeFilters } from "../../../export_fields_utils";
import { getTemplateReportType } from "../../../helpers";
import { Filters as FiltersBoxWrapper } from "../filters";
import { mapValuesInFilterGroup } from "../helpers";

interface FiltersGroupProps {
  name: string;
  label?: string;
  loading: boolean;
  isExpandable?: boolean;
  filters: Filters;
}

export const FiltersGroup = ({
  name,
  label,
  loading,
  isExpandable = true,
  filters
}: FiltersGroupProps): JSX.Element => {
  const {
    updateData,
    state: {
      data: { selectedFilters: allSelectedFilters, exportType, selectedTemplate }
    }
  } = useExportWizardModalContext();
  const [expanded, setExpanded] = useState<boolean>(false);

  const selectedFiltersInGroup = useMemo(
    () =>
      mapValuesInFilterGroup(filters, (familyFilterKey) => (filter) => {
        const selectedGroupFilters = allSelectedFilters[name];
        const selectedFamilyFilters = selectedGroupFilters?.[familyFilterKey];
        return selectedFamilyFilters?.[filter.name!] ?? null;
      }),
    [allSelectedFilters, filters, name]
  );

  const setSelectedFiltersInGroup = useCallback(
    (filters: Filters<Value> | ((filters: Filters<Value>) => Filters<Value>)) => {
      const newFilters = typeof filters === "function" ? filters(selectedFiltersInGroup) : filters;
      updateData({
        selectedFilters: decamelizeFilters({ ...allSelectedFilters, [name]: newFilters })
      });
      if (exportType === ExportType.Template) {
        updateData({
          exportType: ExportType.Custom,
          ...(selectedTemplate ? { reportType: getTemplateReportType(selectedTemplate) } : {})
        });
      }
    },
    [allSelectedFilters, exportType, selectedTemplate, name, selectedFiltersInGroup, updateData]
  );

  const { onFilterChange } = useFilters({
    selectedFilters: selectedFiltersInGroup,
    setSelectedFilters: setSelectedFiltersInGroup
  });

  const FiltersComponent = useMemo(() => {
    const filtersWithValues = mapValuesInFilterGroup(filters, (familyFilterKey) => (filter) => {
      const selectedFamilyFilters = selectedFiltersInGroup?.[familyFilterKey];
      const selectedValue = selectedFamilyFilters?.[filter.name!] ?? null;
      return {
        ...filter,
        value: selectedValue
      };
    });
    return (
      <FiltersBoxWrapper loading={loading} filters={filtersWithValues} onFilterChange={onFilterChange} />
    );
  }, [loading, filters, selectedFiltersInGroup, onFilterChange]);

  return isExpandable && label ? (
    <ExpandableItem label={label} expanded={expanded} onExpand={() => setExpanded((prev) => !prev)}>
      {FiltersComponent}
    </ExpandableItem>
  ) : (
    FiltersComponent
  );
};
